import * as styles from "./SearchResultItem.css";

import { Avatar } from "@frontend/components/atoms/Avatar";
import { Kbd } from "@frontend/components/atoms/Kbd";
import { useLingui } from "@lingui/react";

import { commandPaletteLabels } from "../i18n/labels";
import { getHighlightedTitle } from "../results/highlightText";
import { getResultTitle, getResultTypeDefinition } from "../results/registry";
import type { SearchHit } from "../results/types";

export type SearchResultItemProps = {
  hit: SearchHit;
  highlighted?: boolean;
};

function getInitials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);

  const initials = words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

  return initials || "?";
}

export function SearchResultItem({ hit, highlighted }: SearchResultItemProps) {
  const { _ } = useLingui();

  const definition = getResultTypeDefinition(hit._federation.indexUid);
  const plainTitle = getResultTitle(hit);
  const title = getHighlightedTitle(hit);
  const subtitle = definition?.getSubtitle?.(hit);

  return (
    <div className={styles.row}>
      <Avatar size="sm" fallback={getInitials(plainTitle)} />

      <span className={styles.text}>
        <span className={styles.title}>{title}</span>
        {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
      </span>

      {highlighted && (
        <span className={styles.openHint}>
          <Kbd>↵</Kbd> {_(commandPaletteLabels.openHint)}
        </span>
      )}

      <span className={styles.id}>id:{hit.id}</span>

      {definition?.label && (
        <span className={styles.badge}>{_(definition.label)}</span>
      )}
    </div>
  );
}
